import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls to `#section` after a route change.
 *
 * The browser handles a same-page anchor on its own, but a client-side
 * navigation from `/jobs` to `/#how-it-works` only swaps the route — nothing
 * moves, and the link reads as broken. React Router ships no scroll behaviour
 * by default, so this is it.
 *
 * The offset is not handled here: anchored sections carry `scroll-mt-*`, which
 * `scrollIntoView` already respects, so the sticky header cannot cover the
 * heading it just scrolled to.
 */
export function HashScroll() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;

    // The target belongs to the route being mounted, so it does not exist yet
    // on the frame the location changes. One frame is enough.
    const frame = requestAnimationFrame(() => {
      let target: Element | null = null;
      try {
        target = document.querySelector(hash);
      } catch {
        // A hash that is not a valid selector — `#1`, or anything a stray link
        // produced. Not worth throwing over; there is simply nowhere to go.
        return;
      }
      // Honour the OS setting rather than animating regardless: the same
      // reason index.css disables transitions under prefers-reduced-motion.
      // Guarded because `matchMedia` is absent in some embedded WebViews and
      // in jsdom, and a missing preference must not cost the scroll itself.
      const reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      target?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
    });

    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return null;
}
